import { useEffect, useMemo, useRef, useState } from "react";
import PopBattlePointChart from "./PopBattlePointChart";
import PopBattleLevelChart from "./PopBattleLevelChart";

// Popularity Battle season — split into two independent mount points
// so the Directory page can slot each piece into the right position:
//
//   <PopBattleStatusHero />   — top of page: big battle-status card +
//                               reg image as full-width banner that
//                               fades into whatever renders below.
//
//   <PopBattleRewardsCard />  — later on the page (right before the
//                               filter row on Directory): "POP BATTLE
//                               REWARDS" tag over the hero video.
//
// Both components take `game` and `doc` (pop_battle_assets/{game}).
// Season phase (pre-reg / reg-open / live-battle / between battles /
// done) drives the status label so the battle number is always the
// biggest thing on screen when the season is running.

const BATTLE_COUNT = 10;
const BATTLE_GAP_MS = 3 * 24 * 60 * 60 * 1000;
const BATTLE_LEN_MS = BATTLE_GAP_MS;

const PAGE_BG = "#0A0A0F";

// Game accent colours pulled from the PlayAware app's
// app_dominant_color_map.dart so the pop-market matches the app
// frames exactly (BGMI orange, PUBG blue).
const ACCENTS = {
  BGMI: "#F88200",
  PUBG: "#258BC8",
};

// Per-game palettes for the battle-status card — replaces the
// single gold-only theme. Each palette drives the radial background,
// border, glow shadow, and the label-text tint on the card.
const CARD_THEMES = {
  BGMI: {
    // Deep orange — light peak → mid orange → burnt umber → near-black.
    background:
      "radial-gradient(120% 90% at 20% 15%, #FFC98C 0%, #F88200 25%, #9C4708 55%, #4A2408 85%, #201200 100%)",
    border: "rgba(255,155,70,0.60)",
    glow: "rgba(255,150,50,0.28)",
    innerGlow: "rgba(255,180,90,0.28)",
    outerGlow: "rgba(255,150,50,0.15)",
    numberGlow: "rgba(255,200,120,0.55)",
    dimText: "#3a1c00",
    accentText: "#FFDCA8",
  },
  PUBG: {
    // Deep blue — icy peak → mid blue → indigo → deep navy → near-black.
    background:
      "radial-gradient(120% 90% at 20% 15%, #B8DEF4 0%, #258BC8 25%, #124568 55%, #082333 85%, #030B14 100%)",
    border: "rgba(80,180,240,0.60)",
    glow: "rgba(70,180,255,0.28)",
    innerGlow: "rgba(120,200,255,0.28)",
    outerGlow: "rgba(70,180,255,0.15)",
    numberGlow: "rgba(160,220,255,0.55)",
    dimText: "#001b2e",
    accentText: "#B0DDF6",
  },
};

// Per-game timezone display: BGMI is India-first (IST), PUBG is
// published globally in UTC. Matches how each publisher announces
// their season windows so users don't have to mentally convert.
const TZ_FMTS = {
  BGMI: {
    label: "IST",
    fmt: new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      weekday: "short", day: "2-digit", month: "short",
      hour: "numeric", minute: "2-digit", hour12: true,
    }),
  },
  PUBG: {
    label: "UTC",
    fmt: new Intl.DateTimeFormat("en-GB", {
      timeZone: "UTC",
      weekday: "short", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }),
  },
};
function fmtForGame(d, game) {
  const t = TZ_FMTS[game] || TZ_FMTS.BGMI;
  return t.fmt.format(d).replace(",", " ·");
}
function tzLabel(game) {
  return (TZ_FMTS[game] || TZ_FMTS.BGMI).label;
}
// Legacy IST helper kept for places that don't have a game handy
// (there aren't any today; can be dropped in a later cleanup).
const fmtIST = (d) => TZ_FMTS.BGMI.fmt.format(d).replace(",", " ·");

function fmtCountdown(ms) {
  if (ms <= 0) return "0s";
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (days || hours) parts.push(`${hours}h`);
  if (days || hours || mins) parts.push(`${mins}m`);
  parts.push(`${secs}s`);
  return parts.join(" ");
}

// Pure phase snapshot — usable outside a component (e.g. Directory
// deciding whether to mount the pop-battle sections at all). Doesn't
// tick — the caller supplies `now`, or defaults to Date.now().
export function computeSeasonPhase(doc, now = new Date()) {
  if (!doc) return { phase: null, battleNumber: null };
  const regStart = doc.registrationStartsAt?.toDate?.() || null;
  const regEnd = doc.registrationEndsAt?.toDate?.() || null;
  if (!regEnd) return { phase: null, battleNumber: null };

  if (regStart && now < regStart) return { phase: "pre-reg", battleNumber: 1 };
  if (now < regEnd) return { phase: "reg-open", battleNumber: 1 };

  const battles = Array.from({ length: BATTLE_COUNT }, (_, i) => {
    const startMs = regEnd.getTime() + i * BATTLE_GAP_MS;
    return { n: i + 1, start: new Date(startMs), end: new Date(startMs + BATTLE_LEN_MS) };
  });
  const live = battles.find((b) => now >= b.start && now < b.end);
  if (live) return { phase: "live-battle", battleNumber: live.n };
  const next = battles.find((b) => now < b.start);
  if (next) return { phase: "coming-soon", battleNumber: next.n };
  return { phase: "done", battleNumber: null };
}

// The user-visible phases — everything else (null / pre-reg / done)
// should hide the pop-battle UI from Directory instead of showing a
// stale hero for a season that hasn't started or is already over.
export function isSeasonLive(doc, now = new Date()) {
  const { phase } = computeSeasonPhase(doc, now);
  return phase === "reg-open" || phase === "live-battle" || phase === "coming-soon";
}

// Whether the rewards card should render. Same phases as isSeasonLive
// PLUS "done": users still want to see the current season's rewards
// after all 10 battles finish (previewing what "the last season paid
// out" until a fresh season is scheduled).
export function shouldShowRewards(doc, now = new Date()) {
  const { phase } = computeSeasonPhase(doc, now);
  return phase === "reg-open" || phase === "live-battle" || phase === "coming-soon" || phase === "done";
}

// Post-season notice — shown where the season hero would normally sit
// once all 10 battles are over, so the page says "ended / next season
// coming soon" instead of silently dropping the whole section.
// Renders nothing unless the phase is actually "done".
export function SeasonEndedNotice({ game, doc }) {
  const { phase } = computeSeasonPhase(doc);
  if (phase !== "done") return null;
  const accent = ACCENTS[game] || "#3498db";
  const regEnd = doc.registrationEndsAt?.toDate?.() || null;
  const seasonEnd = regEnd
      ? new Date(regEnd.getTime() + BATTLE_COUNT * BATTLE_GAP_MS)
      : null;
  return (
    <section
      style={{
        margin: "4px 0 24px",
        borderRadius: 18,
        border: `1px solid ${accent}44`,
        background: `linear-gradient(160deg, ${accent}14 0%, rgba(255,255,255,0.02) 40%, rgba(0,0,0,0) 100%), #0e1218`,
        boxShadow: `0 20px 50px rgba(0,0,0,0.55), 0 0 40px ${accent}22`,
        padding: "20px 18px",
        textAlign: "center",
      }}
    >
      <span
        style={{
          display: "inline-block",
          padding: "6px 12px",
          borderRadius: 999,
          background: "rgba(233,63,76,0.16)",
          border: "1px solid rgba(233,63,76,0.45)",
          color: "#ff7b85",
          fontSize: 11, fontWeight: 900, letterSpacing: 1.8,
          marginBottom: 10,
        }}
      >
        ⚔️ {game} · SEASON BATTLE COMPLETE · ALL 10 BATTLES FINISHED
      </span>
      <div style={{ fontSize: 17, fontWeight: 800, color: "#eaeaef", letterSpacing: 0.2 }}>
        The current Popularity Battle season has ended
        {seasonEnd ? ` (${fmtForGame(seasonEnd, game)} ${tzLabel(game)})` : ""}.
      </div>
      <div style={{ marginTop: 6, fontSize: 13, fontWeight: 700, color: accent, letterSpacing: 0.4 }}>
        Next season coming soon — registration dates will appear here.
      </div>
    </section>
  );
}

// Shared season-phase computation. Both components need to know
// which battle is live / coming next so the status matches the
// rewards card's context. `game` picks the timezone used in
// human-readable timestamps (BGMI → IST, PUBG → UTC).
function useSeasonPhase(doc, game) {
  const regStart = doc.registrationStartsAt?.toDate?.() || null;
  const regEnd = doc.registrationEndsAt?.toDate?.() || null;
  const fmt = (d) => fmtForGame(d, game);
  const tz = tzLabel(game);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const battles = useMemo(() => {
    if (!regEnd) return [];
    return Array.from({ length: BATTLE_COUNT }, (_, i) => {
      const startMs = regEnd.getTime() + i * BATTLE_GAP_MS;
      return {
        n: i + 1,
        start: new Date(startMs),
        end: new Date(startMs + BATTLE_LEN_MS),
      };
    });
  }, [regEnd]);

  let phase = null;
  let sub = null;
  let countdownMs = 0;
  let battleNumber = null;

  if (regStart && now < regStart) {
    phase = "pre-reg";
    sub = `Registration opens ${fmt(regStart)} ${tz}`;
    countdownMs = regStart - now;
    battleNumber = 1;
  } else if (regEnd && now < regEnd) {
    phase = "reg-open";
    sub = `Registration open · Battle 1 starts ${fmt(regEnd)} ${tz}`;
    countdownMs = regEnd - now;
    battleNumber = 1;
  } else if (battles.length) {
    const live = battles.find((b) => now >= b.start && now < b.end);
    if (live) {
      phase = "live-battle";
      sub = `Started ${fmt(live.start)} ${tz}`;
      battleNumber = live.n;
      countdownMs = live.end - now;
    } else {
      const next = battles.find((b) => now < b.start);
      if (next) {
        phase = "coming-soon";
        sub = `Starts ${fmt(next.start)} ${tz}`;
        battleNumber = next.n;
        countdownMs = next.start - now;
      } else {
        phase = "done";
        sub = "All 10 battles finished";
      }
    }
  }

  return { phase, sub, countdownMs, battleNumber, now, battles };
}

// ─── REG IMAGE BANNER (mounts at top of Directory) ─────────────────
// Season art + a small phase pill + registration window dates + a
// live countdown so the top of the page tells the user everything
// they need without scrolling to the golden card.
export function PopBattleStatusHero({ game, doc }) {
  const regImg = doc.registrationImageUrl || null;
  if (!regImg) return null;
  const { phase, countdownMs, battles, now } = useSeasonPhase(doc, game);
  const regStart = doc.registrationStartsAt?.toDate?.() || null;
  const regEnd = doc.registrationEndsAt?.toDate?.() || null;
  const accent = ACCENTS[game] || "#3498db";

  // Once registration completes and the season is in battle mode
  // (live-battle / coming-soon / done) the "Registration Window"
  // panel becomes stale — swap in a battle-timeline panel showing
  // the 10 fights with their scheduled times and a countdown for
  // the live / next one.
  const showBattlesPanel =
      phase === "live-battle" || phase === "coming-soon" || phase === "done";
  // Per-game battle naming convention: BGMI = "Battle N", PUBG = "PKN".
  const battleShort = (n) => (game === "PUBG" ? `PK${n}` : `Battle ${n}`);

  const phaseInfo =
    phase === "reg-open"    ? { label: "● REGISTRATION OPEN", color: "#2bb562", bg: "rgba(43,181,98,0.18)" } :
    phase === "pre-reg"     ? { label: "○ REGISTRATION UPCOMING", color: "#a4a7b0", bg: "rgba(180,180,190,0.12)" } :
    phase === "live-battle" ? { label: "● BATTLE LIVE", color: "#2bb562", bg: "rgba(43,181,98,0.18)" } :
    phase === "coming-soon" ? { label: "○ BETWEEN BATTLES", color: "#a4a7b0", bg: "rgba(180,180,190,0.12)" } :
    phase === "done"        ? { label: "SEASON BATTLE ENDED", color: "#ff7b85", bg: "rgba(233,63,76,0.16)" } :
    null;

  // Countdown label depends on which countdown we're actually
  // showing — the top banner focuses on the registration window,
  // so during reg-open we count down to close, during pre-reg to
  // open, and during battle / between / done we hide it (the
  // golden card next to the video already shows the battle timer).
  const timerLabel =
    phase === "reg-open" ? "REGISTRATION CLOSES IN" :
    phase === "pre-reg"  ? "REGISTRATION OPENS IN" :
    null;

  return (
    <section style={{ marginBottom: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2.5, color: accent }}>
          POPULARITY BATTLE
        </span>
        <span
          style={{
            fontSize: 11, fontWeight: 800, letterSpacing: 1.5,
            padding: "3px 9px", borderRadius: 6,
            background: accent, color: "#0d1017",
          }}
        >
          {game}
        </span>
        {phaseInfo && (
          <span
            style={{
              fontSize: 11, fontWeight: 800, letterSpacing: 1.5,
              padding: "3px 12px", borderRadius: 999,
              background: phaseInfo.bg,
              color: phaseInfo.color,
              border: `1px solid ${phaseInfo.color}55`,
            }}
          >
            {phaseInfo.label}
          </span>
        )}
      </div>

      {/* Info bar — either the registration window (pre-reg / reg-
          open) or the battle timeline (once battles kick off).
          Compact panel, one-line on desktop, wraps on mobile. */}
      {!showBattlesPanel && regStart && regEnd && (
        <div
          className="pb-battle-panel"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0,1fr) auto",
            alignItems: "center",
            gap: 14,
            marginBottom: 12,
            padding: "14px 16px",
            borderRadius: 10,
            background: `linear-gradient(90deg, ${accent}18 0%, ${accent}05 65%, transparent 100%)`,
            border: `1px solid ${accent}44`,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10.5, letterSpacing: 1.5, color: "#a4a7b0", fontWeight: 800, marginBottom: 4 }}>
              REGISTRATION WINDOW · {tzLabel(game)}
            </div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "#eaeaef", letterSpacing: 0.2 }}>
              {fmtForGame(regStart, game)}{" "}
              <span style={{ color: accent }}>→</span>{" "}
              {fmtForGame(regEnd, game)}
            </div>
          </div>
          {timerLabel && countdownMs > 0 && (
            <div className="pb-battle-panel-timer" style={{ textAlign: "right", minWidth: 0 }}>
              <div style={{ fontSize: 10.5, letterSpacing: 1.5, color: "#a4a7b0", fontWeight: 800 }}>
                {timerLabel}
              </div>
              <div
                style={{
                  fontFamily:
                    "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace",
                  fontSize: "clamp(20px, 3vw, 26px)",
                  fontWeight: 800, color: "#fff",
                  letterSpacing: 0.8, marginTop: 3,
                  textShadow: `0 0 20px ${accent}66`,
                }}
              >
                {fmtCountdown(countdownMs)}
              </div>
            </div>
          )}
        </div>
      )}

      {showBattlesPanel && battles.length > 0 && (
        <BattleTimelinePanel
          game={game}
          accent={accent}
          battles={battles}
          now={now}
          battleShort={battleShort}
        />
      )}
      <div
        style={{
          position: "relative",
          width: "100%",
          borderRadius: "16px 16px 0 0",
          overflow: "hidden",
          backgroundColor: "#0e1218",
        }}
      >
        {/* Real <img> instead of background-image + cover — that
            combo cropped the sides of the reg image on narrow (mobile)
            viewports. An <img> at width:100% + height:auto scales
            uniformly so nothing is ever cropped horizontally, and the
            container height flexes to the image's natural aspect. */}
        <img
          src={regImg}
          alt={`${game} Popularity Battle registration`}
          style={{
            display: "block",
            width: "100%",
            height: "auto",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute", left: 0, right: 0, top: 0,
            height: 32,
            background: `linear-gradient(180deg, ${PAGE_BG}66 0%, transparent 100%)`,
            pointerEvents: "none",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute", left: 0, right: 0, bottom: 0,
            height: "50%",
            background: `linear-gradient(180deg, transparent 0%, transparent 28%, ${PAGE_BG} 96%)`,
            pointerEvents: "none",
          }}
        />
      </div>
    </section>
  );
}

// One battle at a time — the currently-running fight (which lasts
// its full 3-day window) or, in the gap between two, the next
// upcoming one. Same one-line panel as the registration window
// above but focused on ONE battle's start/end + countdown.
function BattleTimelinePanel({ game, accent, battles, now, battleShort }) {
  const live = battles.find((b) => now >= b.start && now < b.end);
  const next = !live ? battles.find((b) => now < b.start) : null;
  const focus = live || next; // if the season is fully done, focus is null

  if (!focus) {
    return (
      <div
        style={{
          display: "flex", alignItems: "center",
          padding: "10px 14px", marginBottom: 12,
          borderRadius: 10,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.08)",
          color: "#c8c8c8", fontSize: 12, fontWeight: 700, letterSpacing: 1,
        }}
      >
        SEASON BATTLE COMPLETE · all 10 battles finished
      </div>
    );
  }

  const stateLabel = live ? "● BATTLE LIVE" : "○ UP NEXT";
  const stateColor = live ? "#2bb562" : accent;
  const timerLabel = live ? "ENDS IN" : "STARTS IN";
  const timerMs = live ? focus.end - now : focus.start - now;

  return (
    <>
      <div
        className="pb-battle-panel"
        style={{
          marginBottom: 12,
          padding: "14px 16px",
          borderRadius: 10,
          background: `linear-gradient(90deg, ${accent}18 0%, ${accent}05 65%, transparent 100%)`,
          border: `1px solid ${accent}44`,
          display: "grid",
          gridTemplateColumns: "minmax(0,1fr) auto",
          gap: 14,
          alignItems: "center",
        }}
      >
        {/* Left column: battle name (big) + state pill + window */}
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
            <span
              style={{
                fontFamily:
                  "'Rajdhani', system-ui, -apple-system, sans-serif",
                fontSize: "clamp(24px, 4vw, 30px)",
                fontWeight: 700,
                color: "#fff",
                letterSpacing: 1.2,
                lineHeight: 1,
                textShadow: `0 2px 10px rgba(0,0,0,0.4), 0 0 18px ${stateColor}55`,
              }}
            >
              {battleShort(focus.n)}
            </span>
            <span
              style={{
                fontSize: 11, fontWeight: 900, letterSpacing: 1.5,
                padding: "3px 10px", borderRadius: 999,
                background: `${stateColor}22`, color: stateColor,
                border: `1px solid ${stateColor}66`,
              }}
            >
              {stateLabel}
            </span>
          </div>
          <div style={{ fontSize: 12.5, color: "#c8ced5", fontWeight: 600, letterSpacing: 0.2 }}>
            {fmtForGame(focus.start, game)}{" "}
            <span style={{ color: accent }}>→</span>{" "}
            {fmtForGame(focus.end, game)}
            <span style={{ color: "#7a7d86", fontWeight: 700, letterSpacing: 1, marginLeft: 6 }}>
              · {tzLabel(game)}
            </span>
          </div>
        </div>

        {/* Right column: countdown */}
        {timerMs > 0 && (
          <div className="pb-battle-panel-timer" style={{ textAlign: "right", minWidth: 0 }}>
            <div style={{ fontSize: 10.5, letterSpacing: 1.5, color: "#a4a7b0", fontWeight: 800 }}>
              {timerLabel}
            </div>
            <div
              style={{
                fontFamily:
                  "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace",
                fontSize: "clamp(20px, 3vw, 26px)",
                fontWeight: 800, color: "#fff",
                letterSpacing: 0.8, marginTop: 3,
                textShadow: `0 0 20px ${live ? "#2bb56255" : accent + "66"}`,
              }}
            >
              {fmtCountdown(timerMs)}
            </div>
          </div>
        )}
      </div>
      <style>{`
        @media (max-width: 640px) {
          .pb-battle-panel {
            grid-template-columns: minmax(0,1fr) !important;
            gap: 10px !important;
          }
          .pb-battle-panel-timer {
            text-align: left !important;
          }
        }
      `}</style>
    </>
  );
}

// Rewards video that loops just a SEGMENT of the source (e.g. 1:34 →
// 2:26). Behaviour:
//   - By default, the video sits inside the segment forever: on
//     load it seeks to startSec and every time it crosses endSec
//     it snaps back to startSec.
//   - When the user drags the seek bar (or clicks a specific time on
//     the track), the segment loop yields for ONE playthrough — the
//     video plays from wherever the user parked it, all the way to
//     the natural end.
//   - When the video hits its natural end (`ended` event), the
//     segment loop re-arms itself: seek back to startSec and resume
//     playing. So even after a user seek, the loop eventually
//     resumes on its own without any manual intervention.
//   - Pausing / resuming does NOT change any of this — only a real
//     time-bar move counts as user intent to see something else.
function SegmentLoopVideo({ src, mime, startSec, endSec, ...videoProps }) {
  const ref = useRef(null);
  const inSegmentRef = useRef(true);
  const programmaticSeekRef = useRef(false);

  useEffect(() => {
    const v = ref.current;
    if (!v || !Number.isFinite(startSec) || !Number.isFinite(endSec)) return;
    if (endSec <= startSec) return;

    const seekTo = (t) => {
      programmaticSeekRef.current = true;
      try { v.currentTime = t; } catch (_) { /* ignore */ }
    };
    const enterSegment = () => {
      inSegmentRef.current = true;
      seekTo(startSec);
      // Autoplay-safe: muted playback resumes without a gesture. If
      // this ever rejects (e.g. unmute+background tab), we swallow
      // — the user can hit play manually.
      v.play().catch(() => {});
    };

    const onMeta = () => enterSegment();
    const onTime = () => {
      if (!inSegmentRef.current) return;
      if (v.currentTime >= endSec) seekTo(startSec);
    };
    const onSeeking = () => {
      // Only USER seeks count. A programmatic seekTo() also fires
      // `seeking`, so gate on the flag we set before every seekTo.
      if (programmaticSeekRef.current) return;
      inSegmentRef.current = false;
    };
    const onSeeked = () => {
      programmaticSeekRef.current = false;
    };
    // Natural end (video hit its duration): re-arm the segment loop
    // and start over at startSec. This is what makes the user seek
    // a one-shot excursion instead of a permanent opt-out.
    const onEnded = () => enterSegment();

    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("seeking", onSeeking);
    v.addEventListener("seeked", onSeeked);
    v.addEventListener("ended", onEnded);

    // Cache-warmed videos may already have metadata loaded before
    // this effect binds; kick off the segment now in that case.
    if (v.readyState >= 1 && inSegmentRef.current) enterSegment();

    return () => {
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("seeking", onSeeking);
      v.removeEventListener("seeked", onSeeked);
      v.removeEventListener("ended", onEnded);
    };
  }, [startSec, endSec, src]);

  return (
    <video
      ref={ref}
      src={src}
      {...(mime ? { type: mime } : {})}
      {...videoProps}
    />
  );
}

// The three per-slot reward videos (new model). Ordered — this is the
// display order on the page.
const REWARD_SLOTS = [
  { key: "partnerPose", label: "PARTNER POSE" },
  { key: "lobbyTheme", label: "LOBBY THEME" },
  { key: "infoBackground", label: "INFORMATION BACKGROUND" },
];

// ─── REWARDS SECTION — videos + golden battle status card ──────────
// New model: up to three labeled videos (Partner Pose / Lobby Theme /
// Information Background) from doc.rewardsVideos — only slots with an
// upload render. Falls back to the legacy single rewardsVideoUrl /
// rewardsImageUrl until the new videos are uploaded.
export function PopBattleRewardsCard({ game, doc }) {
  const slotVideos = REWARD_SLOTS
      .map((s) => ({ ...s, ...(doc.rewardsVideos?.[s.key] || {}) }))
      .filter((s) => s.url);
  const rewardsVideoUrl = doc.rewardsVideoUrl || null;
  const rewardsVideoMime = doc.rewardsVideoMime || null;
  const rewardsVideoLoopStart = Number.isFinite(doc.rewardsVideoLoopStart)
      ? doc.rewardsVideoLoopStart : null;
  const rewardsVideoLoopEnd = Number.isFinite(doc.rewardsVideoLoopEnd)
      ? doc.rewardsVideoLoopEnd : null;
  const rewardsImg = doc.rewardsImageUrl || null;
  const accent = ACCENTS[game] || "#3498db";

  const hasReward = slotVideos.length > 0 || !!rewardsVideoUrl || !!rewardsImg;
  if (!hasReward) return null;

  return (
    <section style={{ margin: "28px 0" }}>
      {/* Section header — REWARDS pill + game tag. */}
      <div
        style={{
          display: "flex", alignItems: "center", gap: 10,
          marginBottom: 14,
        }}
      >
        <span
          style={{
            padding: "6px 12px",
            borderRadius: 999,
            background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
            color: "#0d1017",
            fontSize: 11.5, fontWeight: 900, letterSpacing: 1.8,
            boxShadow: `0 4px 18px ${accent}66`,
          }}
        >
          🏆 POP BATTLE REWARDS
        </span>
        <span
          style={{
            fontSize: 11, fontWeight: 700, letterSpacing: 1.4,
            color: "#a4a7b0",
          }}
        >
          {game}
        </span>
      </div>

      {/* Two-column layout: video (grows, min 480px) and golden
          battle status card (fixed 340px). Wraps to stacked on
          narrow viewports. */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 20,
          alignItems: "flex-start",
        }}
      >
        {/* Left: reward videos — stacked vertically, each taking the
            full width of this column so the trimmed reward footage
            reads as large hero-sized clips rather than thumbnails. */}
        <div style={{ flex: "1 1 640px", minWidth: 0 }}>
          {slotVideos.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 24,
              }}
            >
              {slotVideos.map((s) => {
                const hasSegment = Number.isFinite(s.loopStart) && Number.isFinite(s.loopEnd) && s.loopEnd > s.loopStart;
                return (
                <div key={s.key} style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13, fontWeight: 900, letterSpacing: 2.2,
                      color: accent, marginBottom: 10,
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{
                      width: "100%",
                      borderRadius: 16,
                      padding: 4,
                      background: `linear-gradient(135deg, ${accent}, ${accent}66)`,
                      boxShadow: `0 20px 50px rgba(0,0,0,0.6), 0 0 60px ${accent}33`,
                      display: "flex",
                    }}
                  >
                    {hasSegment ? (
                    <SegmentLoopVideo
                      src={s.url}
                      mime={s.mime}
                      startSec={s.loopStart}
                      endSec={s.loopEnd}
                      autoPlay
                      muted
                      playsInline
                      controls
                      style={{
                        display: "block", width: "100%", height: "auto",
                        borderRadius: 13, background: "#000",
                      }}
                    />
                    ) : (
                    <video
                      src={s.url}
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls
                      {...(s.mime ? { type: s.mime } : {})}
                      style={{
                        display: "block", width: "100%", height: "auto",
                        borderRadius: 13, background: "#000",
                      }}
                    />
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: 16,
                padding: 4,
                background: `linear-gradient(135deg, ${accent}, ${accent}66)`,
                boxShadow: `0 20px 50px rgba(0,0,0,0.6), 0 0 60px ${accent}33`,
                display: "flex",
              }}
            >
              {rewardsVideoUrl ? (
                rewardsVideoLoopStart != null && rewardsVideoLoopEnd != null ? (
                  <SegmentLoopVideo
                    src={rewardsVideoUrl}
                    mime={rewardsVideoMime}
                    startSec={rewardsVideoLoopStart}
                    endSec={rewardsVideoLoopEnd}
                    autoPlay
                    muted
                    playsInline
                    controls
                    style={{
                      display: "block", width: "100%", height: "auto",
                      borderRadius: 13, background: "#000",
                    }}
                  />
                ) : (
                  <video
                    src={rewardsVideoUrl}
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                    {...(rewardsVideoMime ? { type: rewardsVideoMime } : {})}
                    style={{
                      display: "block", width: "100%", height: "auto",
                      borderRadius: 13, background: "#000",
                    }}
                  />
                )
              ) : (
                <img
                  src={rewardsImg}
                  alt={`${game} Popularity Battle rewards`}
                  style={{
                    display: "block", width: "100%", height: "auto",
                    borderRadius: 13, background: "#000",
                    objectFit: "contain",
                  }}
                />
              )}
            </div>
          )}
        </div>

        {/* Right: themed battle status card (fixed width on desktop,
            capped + tighter padding on mobile so it doesn't stretch
            full-width or feel over-padded when it wraps under the
            video). */}
        <div className="pb-status-slot" style={{ flex: "0 1 340px", minWidth: 260 }}>
          <GoldenBattleStatusCard doc={doc} game={game} />
        </div>
      </div>
      <style>{`
        @media (max-width: 720px) {
          .pb-status-slot {
            width: 100% !important;
            max-width: none !important;
            min-width: 0 !important;
            margin: 0 !important;   /* left-aligned, not centered */
          }
          .pb-status-card {
            padding: 14px 16px !important;
            min-height: 160px !important;   /* shorter — not a square */
          }
        }
      `}</style>
    </section>
  );
}

// ─── GOLDEN BATTLE STATUS CARD ─────────────────────────────────────
// Rich gold-themed card — always sits next to the rewards video so
// the "current battle number" is a permanent, unmissable focal
// element regardless of the season phase. Battle naming is per game:
// BGMI names each fight "Battle 1..10", PUBG names them "PK1..10".
function GoldenBattleStatusCard({ doc, game }) {
  const { phase, sub, countdownMs, battleNumber } = useSeasonPhase(doc, game);
  // Per-game naming: BGMI fights are "BATTLE 1..10", PUBG fights are
  // "PK1..10" (no space, per the in-game convention).
  const battleName = battleNumber
      ? (game === "PUBG" ? `PK${battleNumber}` : `BATTLE ${battleNumber}`)
      : null;

  // Distilled phase strings for the card. The battle number is the
  // biggest character on the card so users immediately see WHICH
  // battle is being counted down, then the status underneath tells
  // them whether it's live or upcoming.
  // Golden-card state pill is binary by design: either the battle is
  // LIVE, or the next one STARTS SOON. Registration-open / between-
  // battles / pre-reg all read as "STARTS SOON" — the reg-image
  // banner above already carries the registration state, so the
  // golden card doesn't repeat it.
  const stateLine =
    phase === "live-battle" ? "LIVE NOW" :
    phase === "done"        ? "SEASON BATTLE COMPLETE" :
    phase                    ? "STARTS SOON" :
    "";

  const timerLabel =
    phase === "live-battle" ? "ENDS IN" :
    phase === "pre-reg"     ? "OPENS IN" :
    phase                    ? "STARTS IN" :
    "";

  const theme = CARD_THEMES[game] || CARD_THEMES.BGMI;
  const stateColor =
    phase === "live-battle" ? "#4de37c" :   // bright green — LIVE reads active
    phase === "done"        ? "#c8c8c8" :
    theme.accentText;                       // themed pale gold/blue for "starts soon"

  // The big battle number — 1..10 during reg / battles, blank for
  // "SEASON COMPLETE" so the card doesn't lie.
  const bigNumber = battleNumber ? String(battleNumber) : "—";
  return (
    <div
      className="pb-status-card"
      style={{
        position: "relative",
        height: "100%",
        minHeight: 340,
        borderRadius: 18,
        padding: "22px 22px 20px",
        // Per-game radial gradient — orange for BGMI, blue for PUBG,
        // matching the PlayAware app's frame colours.
        background: theme.background,
        border: `1px solid ${theme.border}`,
        boxShadow: `0 24px 60px rgba(0,0,0,0.55), 0 0 40px ${theme.innerGlow} inset, 0 0 60px ${theme.outerGlow}`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        color: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Faint diagonal shine so the gold reads metallic, not flat. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0) 20%, rgba(255,255,255,0) 55%, rgba(255,255,255,0.14) 75%, rgba(255,255,255,0) 100%)",
          mixBlendMode: "screen",
          pointerEvents: "none",
        }}
      />
      {/* ── State pill floated top-right ── */}
      {stateLine && (
        <div
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            zIndex: 2,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "5px 12px",
            borderRadius: 999,
            background: "rgba(0,0,0,0.42)",
            border: `1px solid ${stateColor}88`,
            fontSize: 11.5, fontWeight: 900, letterSpacing: 1.6,
            color: stateColor,
            boxShadow: `0 0 20px ${stateColor}44`,
          }}
        >
          <span
            style={{
              width: 8, height: 8,
              borderRadius: "50%",
              background: stateColor,
              boxShadow: `0 0 12px ${stateColor}`,
            }}
          />
          {stateLine}
        </div>
      )}

      {/* ── Top-left: battle name (BATTLE 3 / PK3) ──
          Rajdhani for the HUD gaming feel — condensed geometric,
          reads like a stat callout in Halo / CoD / Valorant. Left-
          aligned and pushed down by ~34px so the top-right state
          pill (absolute, top:14) never collides with it — pill's
          bottom edge sits at ~y=38, name starts at ~y=44. */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          marginTop: 34,
        }}
      >
        <span
          style={{
            fontFamily:
              "'Rajdhani', system-ui, -apple-system, sans-serif",
            fontSize: "clamp(34px, 4.5vw, 48px)",
            fontWeight: 700,
            lineHeight: 1,
            color: "#fff",
            textShadow: `0 2px 8px rgba(0,0,0,0.5), 0 0 22px ${theme.numberGlow}`,
            letterSpacing: 2,
            display: "inline-block",
          }}
        >
          {battleName || "—"}
        </span>
      </div>

      {/* ── Bottom: countdown ── */}
      <div style={{ position: "relative", zIndex: 1, marginTop: 12 }}>
        <div
          style={{
            fontSize: 10.5, letterSpacing: 2, color: theme.dimText,
            fontWeight: 800, opacity: 0.9,
          }}
        >
          {timerLabel || " "}
        </div>
        {countdownMs > 0 ? (
          <div
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: "clamp(22px, 3vw, 30px)",
              fontWeight: 800,
              color: "#fff",
              letterSpacing: 0.5,
              marginTop: 2,
              textShadow: "0 2px 12px rgba(0,0,0,0.5)",
            }}
          >
            {fmtCountdown(countdownMs)}
          </div>
        ) : (
          <div
            style={{
              fontSize: 20, fontWeight: 800, color: "#fff", marginTop: 4,
            }}
          >
            —
          </div>
        )}
        {sub && (
          <div
            style={{
              fontSize: 11.5, color: theme.accentText, marginTop: 6,
              letterSpacing: 0.2,
            }}
          >
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DEFAULT export: full stack, for Calendar-style pages ───────────
// Convenience: renders both pieces sequentially with the reg-image
// banner flowing straight into the rewards card. Use on pages
// (Calendar) that don't have site-data between them. The banner and
// points table stay up year-round; the rewards card and the ended
// notice swap depending on whether the season is still running.
export default function PopBattleSeason({ game, doc }) {
  return (
    <>
      <PopBattleStatusHero game={game} doc={doc} />
      <SeasonEndedNotice game={game} doc={doc} />
      <PopBattleRewardsCard game={game} doc={doc} />
      <PopBattlePointChart game={game} />
      <PopBattleLevelChart game={game} />
    </>
  );
}
